import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/ctem-and-the-asset-inventory-problem';
const BLOG_POST_PUBLISHED_AT = '2026-05-12';
const BLOG_POST_READ_TIME = '4 min read';
const BLOG_POST_AUTHOR = 'Circumvent Team';
/* Same topology as Business Context “Visualize” step; palette tuned for security background */
const FEATURE_IMAGE_SRC = '/images/features/icon_Context-graph-base-on-security.svg';
const BLOG_POST_OG_IMAGE_URL = 'https://circumvent.io/images/features/icon_Context-graph-base-on-security.svg';
const BLOG_POST_OG_TITLE = 'CTEM and the Asset Inventory Problem | Circumvent';
const BLOG_POST_OG_DESCRIPTION =
  'CTEM starts with Scoping, but asset inventories break under inconsistent tags. Naming, stack lineage, and relationship graphs still matter for automated discovery.';

const BlogCtemAssetInventoryPage = () => {
  return (
    <>
      <Helmet>
        <title>{BLOG_POST_OG_TITLE}</title>
        <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
        <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
      </Helmet>
      <SeoSocialTags
        title={BLOG_POST_OG_TITLE}
        description={BLOG_POST_OG_DESCRIPTION}
        url={BLOG_POST_CANONICAL_URL}
        ogType="article"
        articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
        ogImageUrl={BLOG_POST_OG_IMAGE_URL}
      />

      <main className="page page--light blog-post-page">
        <section className="page-section page-section--white" aria-labelledby="ctem-post-heading">
          <article className="page-container blog-post">
            <div className="blog-post__header">
              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
              <h1 id="ctem-post-heading" className="blog-post__title">
                CTEM and the Asset Inventory Problem
              </h1>
              <p className="blog-post__lead">
                Scoping defines what you protect. For most organizations, the inventory that should underpin it is where
                things break.
              </p>

              <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
                <li>Insights</li>
                <li>
                  <time dateTime={BLOG_POST_PUBLISHED_AT}>12 May 2026</time>
                </li>
                <li>{BLOG_POST_READ_TIME}</li>
                <li>By {BLOG_POST_AUTHOR}</li>
              </ul>
            </div>

            <div className="blog-post__layout">
              <div className="blog-post__content">
                <figure className="blog-post__feature-image blog-post__feature-image--full-width-graph">
                  <img
                    src={FEATURE_IMAGE_SRC}
                    alt="Resource relationship graph: ingress, identity, workload, service, role, data store, and related nodes with discovery edges."
                    width={720}
                    height={252}
                    loading="eager"
                    decoding="async"
                  />
                </figure>

                <p>
                  Continuous Threat Exposure Management (CTEM) has gained traction since Gartner introduced it in 2022.
                  The framework is built around five stages, and the first, Scoping, is meant to define what you care about:
                  which assets matter to the business and how critical they are. Scoping is the basis for the next stages of
                  the framework, providing the context that drives prioritization, triage, and reporting.
                </p>
                <p>
                  Yet for most organizations, the asset inventory that should underpin Scoping is exactly where things
                  break.
                </p>

                <h2 id="business-and-engineering-needs">Business and engineering needs</h2>
                <p>
                  Scoping has to serve two audiences. Execs and risk owners need to reason in business terms: which
                  applications matter, how critical they are, what data they hold, what compliance standards apply. Product Engineering and
                  Security Engineering need to reason in technical terms: which services and resources exist, who owns them, how
                  they are connected. Those are two different views for the same technology footprint. An inventory built for
                  one audience only does half the job.
                </p>
                <p>
                  A list of resources might help technical triage but does not give leadership the risk profile of the
                  payment application. A spreadsheet of application names and criticality might support
                  reporting but does not tell engineering who owns the misconfigured bucket. So Scoping, for exposure
                  management, means a model that supports and aligns both views: applications as the business sees them,
                  services as the technical building blocks with ownership.
                </p>

                <h2 id="the-continuous-in-ctem">The &apos;Continuous&apos; in CTEM</h2>
                <p>
                  CTEM is Continuous Threat Exposure Management. In practice, most teams still rely on manual approaches to
                  asset inventory work, refreshed annually. As cloud technology changes, this leaves cloud security engineering
                  teams with stale asset information driving the prioritization of live security risks.
                </p>
                <p>
                  Manual CMDB work and spreadsheets remain common. Once a year, an audit team chases down owners, maps resources
                  to what the business cares about, and tries to keep attributes up to date. The work is stakeholder-heavy and
                  slow. It helps with passing an audit, but as soon as it is done deployments and ownership changes make it
                  stale.
                </p>
                <p>
                  Gartner has reported that only about a quarter of organizations get meaningful value from their CMDBs,
                  and that the majority of CMDB initiatives fail to meet expectations due to data quality and completeness.
                </p>

                <h2 id="why-non-continuous-impacts-ctem">Impacts of a non-continuous approach on CTEM</h2>
                <p>
                  When the inventory is static, fragmented, or built with only one lens, the rest of the framework is
                  compromised.
                </p>
                <p id="prioritization-breaks">
                  Prioritization: Focus on what matters most only works if what matters is defined and current. If the list of
                  crown jewels is outdated or disputed, risk decisions are guesswork.
                </p>
                <p id="ownership-breaks">
                  Ownership and escalation: A critical finding on a resource is only actionable if the owner can be identified.
                  When ownership lives in spreadsheets, response slows and accountability blurs.
                </p>
                <p id="reporting-breaks">
                  Reporting: Executives and boards want to know the risk profile of business-critical applications. When risk is
                  presented as a list of resources or technical components they haven&apos;t heard of, it&apos;s misunderstood.
                </p>
                <p>
                  The asset inventory is not a nice-to-have for CTEM. It is the foundation. When it is weak, stale, or
                  one-sided, the whole program is undermined.
                </p>

                <h2 id="implementing-continuous">Implementing Continuous scope discovery</h2>
                <p>
                  For the CTEM framework to be truly impactful, Scope must remain current with infrastructure (resources,
                  identities, code-to-cloud links) and keep the business lens and engineering lens aligned.
                </p>
                <p>
                  Resource lists and tagging are often the starting point for building an automated asset inventory, but both
                  leave gaps. Different APIs for resource discovery return different results, so even deciding what a resource is
                  can be challenging. Security scanners usually create resource IDs for findings where something does not exist,
                  so those IDs also have to be normalized to something meaningful before prioritization can be applied. Tags rely on
                  conventions being followed and kept up to date, which is a challenging task for large, distributed teams.
                  Some resources do not support tagging at all, so they need to be accounted for too.
                </p>
                <p>
                  While there isn&apos;t a foolproof approach to solving these problems, there are multiple methods we ourselves
                  are exploring to build a more complete view of our customers&apos; cloud environments. These include a robust
                  approach to resource normalization and inference, tagging and naming conventions, shared stacks and templates,
                  as well as trust and access edges.
                </p>
                <p>
                  Once detected, we make these service edges inspectable through infrastructure graphing. This graph view is the
                  foundation for our exposure detection and attack path analysis work.
                </p>
                <p>
                  By detecting services and their boundaries, and rolling them up to business applications, we are able to show
                  not only what a customer&apos;s services and applications look like, but also where their greatest security
                  risks are.
                </p>

                <div className="blog-post__cta">
                  <h3>Talk to us about scope and CTEM</h3>
                  <p>If you are aligning exposure management to how your teams ship, we would welcome a conversation.</p>
                  <Link to="/#contact" className="btn btn-solid">
                    Book a demo
                  </Link>
                </div>

                <Link to="/blog" className="blog-crumb-link">
                  <span aria-hidden="true">←</span> Blog
                </Link>
              </div>

              <aside className="blog-post__sidebar" aria-label="Post navigation">
                <nav className="blog-toc" aria-label="On this page">
                  <p className="blog-toc__title">On this page</p>
                  <ul className="blog-toc__list">
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#business-and-engineering-needs">
                        Business and engineering needs
                      </a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#the-continuous-in-ctem">
                        The &apos;continuous&apos; in CTEM
                      </a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#why-non-continuous-impacts-ctem">
                        Impacts of a non-continuous approach on CTEM
                      </a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#implementing-continuous">
                        Implementing continuous scope discovery
                      </a>
                    </li>
                  </ul>
                </nav>
              </aside>
            </div>
          </article>
        </section>
      </main>
    </>
  );
};

export default BlogCtemAssetInventoryPage;
